import { cs } from '../../lib/currency';
import { useEffect, useState } from 'react';
import { DashboardLayout } from '../../components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import api from '../../services/api';
import { toast } from 'sonner';
import {
  DollarSign,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Users,
  Calendar,
  Eye,
} from 'lucide-react';

const DUTY_LABELS = {
  front_desk: 'Front Desk',
  coaching: 'Coaching',
  academy_management: 'Academy Management'
};

const StaffBilling = () => {
  const [summaries, setSummaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);

  useEffect(() => {
    fetchData();
  }, [selectedMonth]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/admin/staff-billing-summary?month=${selectedMonth}`);
      setSummaries(response.data);
    } catch (error) {
      toast.error('Failed to fetch billing data');
    } finally {
      setLoading(false);
    }
  };

  const handleReviewEntry = async (entryId, status) => {
    try {
      await api.put(`/admin/staff-time-entries/${entryId}/review?status=${status}`);
      toast.success(`Entry ${status}`);
      fetchData();
      if (selectedStaff) {
        const updated = summaries.find(s => s.staff_id === selectedStaff.staff_id);
        setSelectedStaff(updated);
      }
    } catch (error) {
      toast.error('Failed to update entry');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      pending: { color: 'bg-amber-100 text-amber-700', icon: Clock },
      approved: { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      rejected: { color: 'bg-rose-100 text-rose-700', icon: XCircle }
    };
    
    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="h-3 w-3" />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
      options.push({ value, label });
    }
    return options;
  };

  const totalPendingHours = summaries.reduce((sum, s) => sum + s.pending_hours, 0);
  const totalApprovedAmount = summaries.reduce((sum, s) => sum + (s.total_amount || 0), 0);

  if (loading) {
    return (
      <DashboardLayout title="Staff Billing">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Staff Billing">
      <div className="space-y-6" data-testid="staff-billing-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Pending Review</p>
                  <p className="font-heading text-2xl font-bold text-amber-600">{totalPendingHours.toFixed(1)} hrs</p>
                </div>
                <AlertCircle className="h-8 w-8 text-amber-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Total Payable</p>
                  <p className="font-heading text-2xl font-bold text-green-600">{cs()}{totalApprovedAmount.toFixed(2)}</p>
                </div>
                <DollarSign className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card className="border border-slate-200">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-slate-500">Active Staff</p>
                  <p className="font-heading text-2xl font-bold text-slate-900">{summaries.filter(s => s.total_hours > 0).length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Month Filter & Table */}
        <Card className="border border-slate-200">
          <CardHeader className="border-b border-slate-100">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-500" />
                Staff Hours Summary
              </CardTitle>
              <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                <SelectTrigger className="w-48" data-testid="billing-month-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getMonthOptions().map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {summaries.length === 0 ? (
              <div className="p-12 text-center">
                <Users className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">No staff members found</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Staff</TableHead>
                      <TableHead className="text-right">Total Hours</TableHead>
                      <TableHead className="text-right">Pending</TableHead>
                      <TableHead className="text-right">Approved</TableHead>
                      <TableHead className="text-right">Amount Due</TableHead>
                      <TableHead className="w-12"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {summaries.map((summary) => (
                      <TableRow key={summary.staff_id} data-testid={`summary-${summary.staff_id}`}>
                        <TableCell className="font-medium">{summary.staff_name}</TableCell>
                        <TableCell className="text-right">{summary.total_hours.toFixed(1)}</TableCell>
                        <TableCell className="text-right">
                          {summary.pending_hours > 0 ? (
                            <span className="text-amber-600 font-medium">{summary.pending_hours.toFixed(1)}</span>
                          ) : (
                            <span className="text-slate-400">0</span>
                          )}
                        </TableCell>
                        <TableCell className="text-right text-green-600 font-medium">
                          {summary.approved_hours.toFixed(1)}
                        </TableCell>
                        <TableCell className="text-right font-semibold">
                          {summary.total_amount ? `${cs()}${summary.total_amount.toFixed(2)}` : '-'}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedStaff(summary);
                              setDetailDialog(true);
                            }}
                            className="text-blue-600 hover:text-blue-700"
                            data-testid={`view-details-${summary.staff_id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={detailDialog} onOpenChange={setDetailDialog}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">
                {selectedStaff?.staff_name} - Time Entries
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {/* Duty Breakdown */}
              {selectedStaff?.duty_breakdown?.length > 0 && (
                <div className="mb-4 grid grid-cols-3 gap-4">
                  {selectedStaff.duty_breakdown.map((db) => (
                    <div key={db.duty_type} className="p-3 bg-slate-50 rounded-lg">
                      <p className="font-medium text-sm">{DUTY_LABELS[db.duty_type] || db.duty_type}</p>
                      <p className="text-lg font-bold text-slate-900">{db.hours.toFixed(1)} hrs</p>
                      <p className="text-sm text-green-600">{cs()}{db.amount.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              )}

              {selectedStaff?.entries?.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No entries for this month</p>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Duty Type</TableHead>
                      <TableHead>Hours</TableHead>
                      <TableHead>Rate</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="w-32">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {selectedStaff?.entries?.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell>
                          {(() => {
                            // Parse date without timezone conversion
                            const [year, month, day] = entry.date.split('-').map(Number);
                            const date = new Date(year, month - 1, day);
                            return date.toLocaleDateString('en-US', {
                              weekday: 'short',
                              month: 'short',
                              day: 'numeric'
                            });
                          })()}
                        </TableCell>
                        <TableCell>
                          <span className="px-2 py-1 rounded bg-slate-100 text-slate-700 text-xs">
                            {DUTY_LABELS[entry.duty_type] || entry.duty_type}
                          </span>
                        </TableCell>
                        <TableCell className="font-semibold">{entry.hours_worked} hrs</TableCell>
                        <TableCell>{cs()}{entry.hourly_rate?.toFixed(2) || '0.00'}/hr</TableCell>
                        <TableCell className="font-semibold text-green-600">
                          {cs()}{(entry.total_amount ?? entry.amount ?? (entry.hours_worked * (entry.hourly_rate || 0)))?.toFixed(2) || '0.00'}
                        </TableCell>
                        <TableCell>{getStatusBadge(entry.status)}</TableCell>
                        <TableCell>
                          {entry.status === 'pending' && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReviewEntry(entry.id, 'approved')}
                                className="text-green-600 hover:text-green-700 hover:bg-green-50"
                                data-testid={`approve-${entry.id}`}
                              >
                                <CheckCircle className="h-4 w-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReviewEntry(entry.id, 'rejected')}
                                className="text-rose-600 hover:text-rose-700 hover:bg-rose-50"
                                data-testid={`reject-${entry.id}`}
                              >
                                <XCircle className="h-4 w-4" />
                              </Button>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
              
              {selectedStaff && (
                <div className="mt-4 p-4 bg-slate-50 rounded-lg">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                    <div>
                      <p className="text-slate-500">Total Hours</p>
                      <p className="font-semibold">{selectedStaff.total_hours.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Approved</p>
                      <p className="font-semibold text-green-600">{selectedStaff.approved_hours.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Pending</p>
                      <p className="font-semibold text-amber-600">{selectedStaff.pending_hours.toFixed(1)}</p>
                    </div>
                    <div>
                      <p className="text-slate-500">Amount Due</p>
                      <p className="font-semibold text-green-600">
                        {cs()}{selectedStaff.total_amount?.toFixed(2) || '0.00'}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default StaffBilling;
